<template>
  <BgImageLayout title="Member Settings" :page="__guide.memberSettings" icon-color="white">
    <q-card color="white" text-color="dark-grey">
      <q-card-section>
        <div class="row q-col-gutter-md">
          <div class="col-xs-5">
            <q-card flat>
              <q-card-section>
                Member Setup
              </q-card-section>
              <q-card-section>
                <div class="row q-col-gutter-lg">
                  <q-input v-model="formData.name" stack-label label="Library Name" class="col-xs-6" outlined />
                  <q-input v-model="formData.abbreviation" label="Abbreviation" class="col-xs-6" outlined />
                  <q-input v-model="formData.alternative_name" label="Alternative name" class="col-xs-6" outlined />
                  <q-input v-model="formData.vat" label="VAT / Tax Id" class="col-xs-6" outlined />
                  <q-input v-model="formData.ringgold_id" label="Ringgold Id" class="col-xs-6" outlined />
                  <q-input v-model="formData.website" label="Website" class="col-xs-6" outlined />
                  <q-input v-model="formData.repository" label="Repository" class="col-xs-6" outlined />
                </div>
              </q-card-section>
            </q-card>

            <q-card flat>
              <q-card-section>
                <div class="row q-col-gutter-lg">
                  <div v-for="(address, index) of formData.addresses" :key="`address_${index}`" class="col-xs-6">
                    <address-form
                      :show-title="true"
                      :address-setting="address"
                      :can-copy-address="address.address_type.name !== 'main'"
                      :address-to-copy="address.address_type.name !== 'main' ? addressToCopy : null"
                      @address-updated="updateAddresses($event, index)"
                    />
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>

          <div class="col-xs-7">
            <q-card flat>
              <q-card-section class="flex justify-between">
                <span>Formatting settings</span>
                <q-btn color="tertiary" size="sm" @click="process()">Save</q-btn>
              </q-card-section>

              <q-card-section>
                <div class="row q-col-gutter-lg">
                  <div class="col-xs-6">
                    <enhanced-q-select
                      id="locale"
                      v-model="formData.locale"
                      :options="localeOptions"
                      label="Language"
                    />
                  </div>

                  <div class="col-xs-6">
                    <enhanced-q-select
                      id="currency"
                      v-model="formData.currency"
                      disable
                      :options="currencies"
                      label="Currency"
                    />
                  </div>

                  <div class="col-xs-3">
                    <enhanced-q-select
                      id="thousands_separator"
                      v-model="formData.thousand_separator"
                      :options="thousandsSeparatorOptions"
                      label="Thousands separator"
                    />
                  </div>

                  <div class="col-xs-3">
                    <enhanced-q-select
                      id="decimal_separator"
                      v-model="formData.decimal_separator"
                      :options="decimalSeparatorOptions"
                      label="Decimal separator"
                    />
                  </div>

                  <q-input
                    v-model="formattedNumber"
                    class="col-xs-3"
                    readonly
                    stack-label
                    label="Sample number output"
                    disable
                    outlined
                  />

                  <div class="col-xs-6">
                    <enhanced-q-select
                      id="date_format"
                      v-model="formData.date_format"
                      :options="dateFormats"
                      label="Date format"
                    />
                  </div>

                  <q-input
                    v-model="formattedDate"
                    class="col-xs-6"
                    readonly
                    stack-label
                    label="Sample date output"
                    disable
                    outlined
                  />

                  <div class="col-xs-6">
                    <enhanced-q-select
                      id="timezone"
                      v-model="formData.timezone"
                      :options="timezoneOptions"
                      label="Timezone"
                    />
                  </div>
                </div>
              </q-card-section>
            </q-card>

            <q-card flat>
              <q-card-section>
                Decision Deadline
                <q-separator class="q-mt-sm q-mb-sm" />
                <span class="text-caption text-grey-6">{{ cancellationReminderHelper }}</span>
              </q-card-section>

              <q-card-section>
                <div class="row q-col-gutter-lg">
                  <q-input
                    v-model.number="formData.cancellation_reminder_days_before_start_date"
                    type="number"
                    min="0"
                    stack-label
                    label="Display days before decision deadline"
                    outlined
                    class="col-xs-6"
                  />
                </div>
              </q-card-section>
            </q-card>

            <ChartRolloverDate ref="chartRolloverDate" />

            <!-- Don't show if event is 1 (January) -->
            <FpStartMonth ref="fpStartMonth" @selected-month="showFpAbbreviation = $event !== 1" />

            <FpAbbreviation v-if="showFpAbbreviation" ref="fpAbbreviation" />

            <AutoDeadline ref="autoDeadline" :value="settings.subscription_deadline_notification" />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </BgImageLayout>
</template>

<script>
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import { cloneDeep, padStart } from 'lodash'
import moment from 'moment-timezone'
import { mapState } from 'vuex'
// Components
import EnhancedQSelect from '@/components/general/EnhancedQSelect'
import AddressForm from '@/components/lm-blocks/Address'
import ChartRolloverDate from './components/ChartRolloverDate'
import FpStartMonth from './components/FpStartMonth'
import FpAbbreviation from './components/FpAbbreviation'
import AutoDeadline from './components/AutoDeadline.vue'

export default {
  components: {
    EnhancedQSelect,
    AddressForm,
    ChartRolloverDate,
    FpStartMonth,
    FpAbbreviation,
    AutoDeadline
  },
  data() {
    return {
      addressToCopy: null,
      formData: null,
      showFpAbbreviation: false
    }
  },
  computed: {
    sampleDate() {
      return moment(Date.now()).format('YYYY-MM-DD')
    },
    formattedDate() {
      return this.formatDateForCustomDisplay(this.sampleDate, this.formData.date_format)
    },
    formattedNumber() {
      return this.formatInputNumber(
        `1${this.thousandsSeparator.value}000${this.decimalSeparator.value}689`,
        2,
        this.formData.decimal_separator,
        this.formData.thousand_separator
      )
    },
    ...mapState({
      settings: state => state.settings,
      localeOptions: state => state.settings.options.locales,
      dateFormats: state => state.settings.options.date_formats,
      decimalSeparatorOptions: state => state.settings.options.decimal_separators,
      thousandsSeparatorOptions: state => state.settings.options.thousand_separators,
      timezones: state => state.settings.options.timezones
    }),
    timezoneOptions() {
      return Object.keys(this.timezones).map(key => {
        return { label: this.timezones[key], value: key }
      })
    }
  },
  watch: {
    'formData.cancellation_reminder_days_before_start_date'(val) {
      this.formData.cancellation_reminder_days_before_start_date = Number(val)
    }
  },
  created() {
    this.formData = new LmFormObject({
      currency: this.settings.currency,
      name: this.settings.name,
      abbreviation: this.settings.abbreviation,
      alternative_name: this.settings.alternative_name,
      vat: this.settings.vat,
      ringgold_id: this.settings.ringgold_id,
      website: this.settings.website,
      repository: this.settings.repository,
      date_format: this.settings.date_format,
      decimal_separator: this.settings.decimal_separator,
      thousand_separator: this.settings.thousand_separator,
      locale: this.settings.locale,
      timezone: this.settings.timezone,
      cancellation_reminder_days_before_start_date: this.settings.cancellation_reminder_days_before_start_date,
      addresses: this.settings.addresses
    })
    this.addressToCopy = this.formData.addresses.find(address => address.address_type.name === 'main')
    this.cancellationReminderHelper = `How many days before subscription periods' decision deadlines should
        decision reminders be displayed on the dashboard`
  },
  methods: {
    process() {
      let data = cloneDeep(this.formData)
      delete data.originalData
      delete data.errors
      delete data.currency

      data.chart_date_switch = this.$refs.chartRolloverDate.getData()
      data.financial_period_start_month = this.$refs.fpStartMonth.getData()
      if (this.showFpAbbreviation) {
        data.financial_period_display_using_date = this.$refs.fpAbbreviation.getData()
      }
      data.subscription_deadline_notification = this.$refs.autoDeadline.getData()

      this.sendData(data)
    },
    sendData(data) {
      return this.$store.dispatch('updateMemberSettings', data)
    },
    updateAddresses(address, index) {
      let addresses = cloneDeep(this.formData.addresses)
      addresses[index] = address
      this.$set(this.formData, 'addresses', addresses)
      if (address.address_type.name === 'main') this.$set(this, 'addressToCopy', address)
    }
  }
}
</script>

<style lang="stylus" scoped>
.stack-label {
  font-size 0.75rem;
}
</style>
